/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Zorgvraagtypering API
 * API for managing zorgvraagtypering (care typing) within zorgtrajecten.  Each zorgtraject must have a zorgvraagtypering (ZT01-ZT21) for billing purposes. When a client\'s situation changes during treatment, a herijking (reassessment) can be performed which closes the current typering and creates a new one.  All operations are scoped to the authenticated user\'s organization. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * Every property is optional: a correction states only what was wrong. An omitted property keeps its stored value.  `isHerijking` and `previousTyperingId` are deliberately absent and are never written by this operation — a correction does not turn a first typering into a herijking, and does not re-point one at a different predecessor. 
 */
export interface CorrigeerZorgvraagtyperingRequest {
    /**
     * Corrected zorgvraagtype. Must be valid on the corrected startdatum per CL0007-NZA. Omit to keep the stored code. 
     */
    'code'?: ZorgvraagtyperingCode | null;
    /**
     * Corrected ingangsdatum. May move backwards, including before today; when an earlier typering exists it must stay after that typering\'s startdatum. Omit to keep the stored date. 
     */
    'startdatum'?: string;
    /**
     * Optional reason for the correction
     */
    'reden'?: string | null;
}


export interface HerijkingRequest {
    'nieuweCode': ZorgvraagtyperingCode;
    /**
     * Start date for the new typering (ingangsdatum). Must be > startdatum of current typering. Future dates are allowed to schedule changes in advance. 
     */
    'startdatumNieuwe': string;
    /**
     * Optional reason for the reassessment
     */
    'reden'?: string | null;
}


/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    'field'?: string;
    'message'?: string;
}
/**
 * Zorgvraagtypering codes as defined by NZa, published as Vektis codelijst CL0007-NZA (Diagnosecodelijst 031). 20 codes: the numbering jumps ZT08 -> ZT10. 
 */

export const ZorgvraagtyperingCode = {
    Zt01: 'ZT01',
    Zt02: 'ZT02',
    Zt03: 'ZT03',
    Zt04: 'ZT04',
    Zt05: 'ZT05',
    Zt06: 'ZT06',
    Zt07: 'ZT07',
    Zt08: 'ZT08',
    Zt10: 'ZT10',
    Zt11: 'ZT11',
    Zt12: 'ZT12',
    Zt13: 'ZT13',
    Zt14: 'ZT14',
    Zt15: 'ZT15',
    Zt16: 'ZT16',
    Zt17: 'ZT17',
    Zt18: 'ZT18',
    Zt19: 'ZT19',
    Zt20: 'ZT20',
    Zt21: 'ZT21'
} as const;

export type ZorgvraagtyperingCode = typeof ZorgvraagtyperingCode[keyof typeof ZorgvraagtyperingCode];


export interface ZorgvraagtyperingCodeInfo {
    'code': ZorgvraagtyperingCode;
    /**
     * Official NZa \"Naam zorgvraagtype\" for this code
     */
    'description': string;
    'hoofdgroep'?: ZorgvraagtyperingHoofdgroep;
    /**
     * The hoofdgroep exactly as CL0007-NZA states it. Absent for a code that is not on the published list. 
     */
    'hoofdgroepDisplayName'?: string;
}


export interface ZorgvraagtyperingCodeListResponse {
    /**
     * All zorgvraagtypering codes
     */
    'codes': Array<ZorgvraagtyperingCodeInfo>;
}
export interface ZorgvraagtyperingHistoryResponse {
    /**
     * Zorgtraject this history belongs to
     */
    'zorgtrajectId': string;
    'current'?: ZorgvraagtyperingResponse;
    /**
     * All typeringen ordered by startdatum descending (most recent first)
     */
    'history': Array<ZorgvraagtyperingResponse>;
    /**
     * Number of herijkingen performed. Computed for display in the Scrivio app.
     */
    'herijkingCount': number;
}
/**
 * Hoofdgroep of a zorgvraagtype, as published in Vektis codelijst CL0007-NZA (column `Toelichting 1`): - X: Psychische aandoening (ZT01-ZT08) - Y: psychotische stoornis (ZT10-ZT17) - Z: Cognitieve beperking (ZT18-ZT21) 
 */

export const ZorgvraagtyperingHoofdgroep = {
    X: 'X',
    Y: 'Y',
    Z: 'Z'
} as const;

export type ZorgvraagtyperingHoofdgroep = typeof ZorgvraagtyperingHoofdgroep[keyof typeof ZorgvraagtyperingHoofdgroep];


export interface ZorgvraagtyperingResponse {
    /**
     * Zorgvraagtypering unique identifier
     */
    'id': string;
    /**
     * Zorgtraject this typering belongs to
     */
    'zorgtrajectId': string;
    /**
     * Organization this typering belongs to
     */
    'organizationId': string;
    'code': ZorgvraagtyperingCode;
    /**
     * Official NZa \"Naam zorgvraagtype\" for this code
     */
    'codeDescription': string;
    'hoofdgroep'?: ZorgvraagtyperingHoofdgroep;
    /**
     * The hoofdgroep exactly as CL0007-NZA states it. Absent for a code that is not on the published list. 
     */
    'hoofdgroepDisplayName'?: string;
    /**
     * Start date of this typering
     */
    'startdatum': string;
    /**
     * End date of this typering (null if still active)
     */
    'einddatum'?: string | null;
    /**
     * Whether this is a herijking (reassessment)
     */
    'isHerijking': boolean;
    /**
     * Link to previous typering (if herijking)
     */
    'previousTyperingId'?: string | null;
    /**
     * The reason recorded with this typering, if any — the herijking\'s reason on a herijking, the correction\'s on one that was corrected. Named for the column, which was named for the only writer it had when it was added. 
     */
    'herijkingReden'?: string | null;
    /**
     * Whether this is the current active typering (einddatum is null)
     */
    'isActive': boolean;
    /**
     * Timestamp when typering was created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * User ID who created this typering. A Scrivio user id. Outside the app it can only be matched against the `userId` that getClientBehandelaars returns.
     */
    'createdBy': string;
    /**
     * Name of the user who created this typering
     */
    'createdByName': string;
}



/**
 * ReferenceDataApi - axios parameter creator
 */
export const ReferenceDataApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns all zorgvraagtypering codes (ZT01-ZT21) with their official NZa description and their hoofdgroep as published in Vektis codelijst CL0007-NZA. Used for dropdown population in forms. 
         * @summary List all zorgvraagtypering codes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listZorgvraagtyperingCodes: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/reference-data/zorgvraagtypering-codes`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ReferenceDataApi - functional programming interface
 */
export const ReferenceDataApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ReferenceDataApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns all zorgvraagtypering codes (ZT01-ZT21) with their official NZa description and their hoofdgroep as published in Vektis codelijst CL0007-NZA. Used for dropdown population in forms. 
         * @summary List all zorgvraagtypering codes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listZorgvraagtyperingCodes(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgvraagtyperingCodeListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listZorgvraagtyperingCodes(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ReferenceDataApi.listZorgvraagtyperingCodes']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ReferenceDataApi - factory interface
 */
export const ReferenceDataApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ReferenceDataApiFp(configuration)
    return {
        /**
         * Returns all zorgvraagtypering codes (ZT01-ZT21) with their official NZa description and their hoofdgroep as published in Vektis codelijst CL0007-NZA. Used for dropdown population in forms. 
         * @summary List all zorgvraagtypering codes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listZorgvraagtyperingCodes(options?: RawAxiosRequestConfig): AxiosPromise<ZorgvraagtyperingCodeListResponse> {
            return localVarFp.listZorgvraagtyperingCodes(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ReferenceDataApi - object-oriented interface
 */
export class ReferenceDataApi extends BaseAPI {
    /**
     * Returns all zorgvraagtypering codes (ZT01-ZT21) with their official NZa description and their hoofdgroep as published in Vektis codelijst CL0007-NZA. Used for dropdown population in forms. 
     * @summary List all zorgvraagtypering codes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listZorgvraagtyperingCodes(options?: RawAxiosRequestConfig) {
        return ReferenceDataApiFp(this.configuration).listZorgvraagtyperingCodes(options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ZorgvraagtyperingApi - axios parameter creator
 */
export const ZorgvraagtyperingApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Corrects the running typering in place. This is **not** `herijken`: appending a typering says the zorgvraag was re-assessed and changed as of a date, correcting says the stored row never described reality. Conflating them inflates the herijking history with clinical events that never happened, on a field that is declared — codelijst `031` on the restitutienota (#3516) and in the GDS801 GGZ Diagnose block (#3533).  A correction therefore leaves `isHerijking` and `previousTyperingId` exactly as stored and creates no row.  Business rules: - The startdatum may move **backwards**, including before today. Since #4094 every   consult resolves the typering covering its own service date, so a startdatum recorded   a week late silently declares the wrong `031` value for every consult in the gap —   which is what this endpoint exists to fix - A corrected startdatum that reaches onto or past the start of an earlier typering   **replaces** it: that typering is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a herijking has   already been appended (#4516). Reaching *into* an earlier typering without covering   its start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - The corrected code must be valid on the corrected startdatum per CL0007-NZA - Omitted properties keep their stored value  Only the current (latest) typering is corrected; an earlier one is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current zorgvraagtypering
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerZorgvraagtyperingRequest} corrigeerZorgvraagtyperingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        corrigeerZorgvraagtypering: async (zorgtrajectId: string, corrigeerZorgvraagtyperingRequest: CorrigeerZorgvraagtyperingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('corrigeerZorgvraagtypering', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'corrigeerZorgvraagtyperingRequest' is not null or undefined
            assertParamExists('corrigeerZorgvraagtypering', 'corrigeerZorgvraagtyperingRequest', corrigeerZorgvraagtyperingRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/zorgvraagtypering/huidige`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PATCH', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(corrigeerZorgvraagtyperingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the current zorgvraagtypering and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). Includes herijking count for summary display. 
         * @summary Get zorgvraagtypering history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgvraagtyperingHistory: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('getZorgvraagtyperingHistory', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/zorgvraagtypering`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Performs a herijking (reassessment) of the zorgvraagtypering. Creates a new typering with the specified code and start date. The end date of the current typering is derived from the new typering\'s startdatum - 1 day.  First-time mode: when the zorgtraject has no current typering yet, this endpoint creates the initial typering instead of a reassessment.  Business rules (reassessment): - Startdatum of new typering must be > startdatum of current typering - Future dates are allowed (schedule changes in advance) - New code can be the same as old code (reassessed, same conclusion) - Multiple herijkingen are allowed (no limit) 
         * @summary Perform herijking (reassessment) or set initial typering
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {HerijkingRequest} herijkingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        herijkZorgvraagtypering: async (zorgtrajectId: string, herijkingRequest: HerijkingRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('herijkZorgvraagtypering', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'herijkingRequest' is not null or undefined
            assertParamExists('herijkZorgvraagtypering', 'herijkingRequest', herijkingRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/zorgvraagtypering/herijken`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(herijkingRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes the running typering, which was recorded in error. The preceding one becomes current again; when it was the only one the zorgtraject is left without a typering — the state the initial typering exists for, and which `billing.missing-zorgvraagtypering` reports on.  This is the only way to undo an entry: appending a typering cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current zorgvraagtypering
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwijderZorgvraagtypering: async (zorgtrajectId: string, reden?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('verwijderZorgvraagtypering', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/zorgvraagtypering/huidige`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (reden !== undefined) {
                localVarQueryParameter['reden'] = reden;
            }

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ZorgvraagtyperingApi - functional programming interface
 */
export const ZorgvraagtyperingApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ZorgvraagtyperingApiAxiosParamCreator(configuration)
    return {
        /**
         * Corrects the running typering in place. This is **not** `herijken`: appending a typering says the zorgvraag was re-assessed and changed as of a date, correcting says the stored row never described reality. Conflating them inflates the herijking history with clinical events that never happened, on a field that is declared — codelijst `031` on the restitutienota (#3516) and in the GDS801 GGZ Diagnose block (#3533).  A correction therefore leaves `isHerijking` and `previousTyperingId` exactly as stored and creates no row.  Business rules: - The startdatum may move **backwards**, including before today. Since #4094 every   consult resolves the typering covering its own service date, so a startdatum recorded   a week late silently declares the wrong `031` value for every consult in the gap —   which is what this endpoint exists to fix - A corrected startdatum that reaches onto or past the start of an earlier typering   **replaces** it: that typering is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a herijking has   already been appended (#4516). Reaching *into* an earlier typering without covering   its start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - The corrected code must be valid on the corrected startdatum per CL0007-NZA - Omitted properties keep their stored value  Only the current (latest) typering is corrected; an earlier one is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current zorgvraagtypering
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerZorgvraagtyperingRequest} corrigeerZorgvraagtyperingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async corrigeerZorgvraagtypering(zorgtrajectId: string, corrigeerZorgvraagtyperingRequest: CorrigeerZorgvraagtyperingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgvraagtyperingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.corrigeerZorgvraagtypering(zorgtrajectId, corrigeerZorgvraagtyperingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgvraagtyperingApi.corrigeerZorgvraagtypering']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the current zorgvraagtypering and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). Includes herijking count for summary display. 
         * @summary Get zorgvraagtypering history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getZorgvraagtyperingHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgvraagtyperingHistoryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getZorgvraagtyperingHistory(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgvraagtyperingApi.getZorgvraagtyperingHistory']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Performs a herijking (reassessment) of the zorgvraagtypering. Creates a new typering with the specified code and start date. The end date of the current typering is derived from the new typering\'s startdatum - 1 day.  First-time mode: when the zorgtraject has no current typering yet, this endpoint creates the initial typering instead of a reassessment.  Business rules (reassessment): - Startdatum of new typering must be > startdatum of current typering - Future dates are allowed (schedule changes in advance) - New code can be the same as old code (reassessed, same conclusion) - Multiple herijkingen are allowed (no limit) 
         * @summary Perform herijking (reassessment) or set initial typering
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {HerijkingRequest} herijkingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async herijkZorgvraagtypering(zorgtrajectId: string, herijkingRequest: HerijkingRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ZorgvraagtyperingResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.herijkZorgvraagtypering(zorgtrajectId, herijkingRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgvraagtyperingApi.herijkZorgvraagtypering']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes the running typering, which was recorded in error. The preceding one becomes current again; when it was the only one the zorgtraject is left without a typering — the state the initial typering exists for, and which `billing.missing-zorgvraagtypering` reports on.  This is the only way to undo an entry: appending a typering cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current zorgvraagtypering
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verwijderZorgvraagtypering(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verwijderZorgvraagtypering(zorgtrajectId, reden, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ZorgvraagtyperingApi.verwijderZorgvraagtypering']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ZorgvraagtyperingApi - factory interface
 */
export const ZorgvraagtyperingApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ZorgvraagtyperingApiFp(configuration)
    return {
        /**
         * Corrects the running typering in place. This is **not** `herijken`: appending a typering says the zorgvraag was re-assessed and changed as of a date, correcting says the stored row never described reality. Conflating them inflates the herijking history with clinical events that never happened, on a field that is declared — codelijst `031` on the restitutienota (#3516) and in the GDS801 GGZ Diagnose block (#3533).  A correction therefore leaves `isHerijking` and `previousTyperingId` exactly as stored and creates no row.  Business rules: - The startdatum may move **backwards**, including before today. Since #4094 every   consult resolves the typering covering its own service date, so a startdatum recorded   a week late silently declares the wrong `031` value for every consult in the gap —   which is what this endpoint exists to fix - A corrected startdatum that reaches onto or past the start of an earlier typering   **replaces** it: that typering is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a herijking has   already been appended (#4516). Reaching *into* an earlier typering without covering   its start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - The corrected code must be valid on the corrected startdatum per CL0007-NZA - Omitted properties keep their stored value  Only the current (latest) typering is corrected; an earlier one is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
         * @summary Correct the current zorgvraagtypering
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {CorrigeerZorgvraagtyperingRequest} corrigeerZorgvraagtyperingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        corrigeerZorgvraagtypering(zorgtrajectId: string, corrigeerZorgvraagtyperingRequest: CorrigeerZorgvraagtyperingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ZorgvraagtyperingResponse> {
            return localVarFp.corrigeerZorgvraagtypering(zorgtrajectId, corrigeerZorgvraagtyperingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the current zorgvraagtypering and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). Includes herijking count for summary display. 
         * @summary Get zorgvraagtypering history for zorgtraject
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getZorgvraagtyperingHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<ZorgvraagtyperingHistoryResponse> {
            return localVarFp.getZorgvraagtyperingHistory(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * Performs a herijking (reassessment) of the zorgvraagtypering. Creates a new typering with the specified code and start date. The end date of the current typering is derived from the new typering\'s startdatum - 1 day.  First-time mode: when the zorgtraject has no current typering yet, this endpoint creates the initial typering instead of a reassessment.  Business rules (reassessment): - Startdatum of new typering must be > startdatum of current typering - Future dates are allowed (schedule changes in advance) - New code can be the same as old code (reassessed, same conclusion) - Multiple herijkingen are allowed (no limit) 
         * @summary Perform herijking (reassessment) or set initial typering
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {HerijkingRequest} herijkingRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        herijkZorgvraagtypering(zorgtrajectId: string, herijkingRequest: HerijkingRequest, options?: RawAxiosRequestConfig): AxiosPromise<ZorgvraagtyperingResponse> {
            return localVarFp.herijkZorgvraagtypering(zorgtrajectId, herijkingRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes the running typering, which was recorded in error. The preceding one becomes current again; when it was the only one the zorgtraject is left without a typering — the state the initial typering exists for, and which `billing.missing-zorgvraagtypering` reports on.  This is the only way to undo an entry: appending a typering cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
         * @summary Remove the current zorgvraagtypering
         * @param {string} zorgtrajectId Zorgtraject UUID
         * @param {string} [reden] Optional reason, recorded on the audit entry
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verwijderZorgvraagtypering(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.verwijderZorgvraagtypering(zorgtrajectId, reden, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ZorgvraagtyperingApi - object-oriented interface
 */
export class ZorgvraagtyperingApi extends BaseAPI {
    /**
     * Corrects the running typering in place. This is **not** `herijken`: appending a typering says the zorgvraag was re-assessed and changed as of a date, correcting says the stored row never described reality. Conflating them inflates the herijking history with clinical events that never happened, on a field that is declared — codelijst `031` on the restitutienota (#3516) and in the GDS801 GGZ Diagnose block (#3533).  A correction therefore leaves `isHerijking` and `previousTyperingId` exactly as stored and creates no row.  Business rules: - The startdatum may move **backwards**, including before today. Since #4094 every   consult resolves the typering covering its own service date, so a startdatum recorded   a week late silently declares the wrong `031` value for every consult in the gap —   which is what this endpoint exists to fix - A corrected startdatum that reaches onto or past the start of an earlier typering   **replaces** it: that typering is removed and this one takes over from the corrected   date. That is how \"it applied from the beginning\" is recorded once a herijking has   already been appended (#4516). Reaching *into* an earlier typering without covering   its start removes nothing — it simply ends the day before this one begins - The corrected startdatum may not fall before the zorgtraject\'s own startdatum (422) - The corrected code must be valid on the corrected startdatum per CL0007-NZA - Omitted properties keep their stored value  Only the current (latest) typering is corrected; an earlier one is never edited, it is either left alone or removed whole by a correction that covers its start. Each removal is written to the dossier audit log naming the correction that replaced it (ADR-011). 
     * @summary Correct the current zorgvraagtypering
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {CorrigeerZorgvraagtyperingRequest} corrigeerZorgvraagtyperingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public corrigeerZorgvraagtypering(zorgtrajectId: string, corrigeerZorgvraagtyperingRequest: CorrigeerZorgvraagtyperingRequest, options?: RawAxiosRequestConfig) {
        return ZorgvraagtyperingApiFp(this.configuration).corrigeerZorgvraagtypering(zorgtrajectId, corrigeerZorgvraagtyperingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the current zorgvraagtypering and complete history for a zorgtraject. History is ordered by startdatum descending (most recent first). Includes herijking count for summary display. 
     * @summary Get zorgvraagtypering history for zorgtraject
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getZorgvraagtyperingHistory(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return ZorgvraagtyperingApiFp(this.configuration).getZorgvraagtyperingHistory(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Performs a herijking (reassessment) of the zorgvraagtypering. Creates a new typering with the specified code and start date. The end date of the current typering is derived from the new typering\'s startdatum - 1 day.  First-time mode: when the zorgtraject has no current typering yet, this endpoint creates the initial typering instead of a reassessment.  Business rules (reassessment): - Startdatum of new typering must be > startdatum of current typering - Future dates are allowed (schedule changes in advance) - New code can be the same as old code (reassessed, same conclusion) - Multiple herijkingen are allowed (no limit) 
     * @summary Perform herijking (reassessment) or set initial typering
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {HerijkingRequest} herijkingRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public herijkZorgvraagtypering(zorgtrajectId: string, herijkingRequest: HerijkingRequest, options?: RawAxiosRequestConfig) {
        return ZorgvraagtyperingApiFp(this.configuration).herijkZorgvraagtypering(zorgtrajectId, herijkingRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes the running typering, which was recorded in error. The preceding one becomes current again; when it was the only one the zorgtraject is left without a typering — the state the initial typering exists for, and which `billing.missing-zorgvraagtypering` reports on.  This is the only way to undo an entry: appending a typering cannot express \"this should never have been recorded\". The removal is written to the dossier audit log, so the mistake and its correction both stay visible (ADR-011). 
     * @summary Remove the current zorgvraagtypering
     * @param {string} zorgtrajectId Zorgtraject UUID
     * @param {string} [reden] Optional reason, recorded on the audit entry
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verwijderZorgvraagtypering(zorgtrajectId: string, reden?: string, options?: RawAxiosRequestConfig) {
        return ZorgvraagtyperingApiFp(this.configuration).verwijderZorgvraagtypering(zorgtrajectId, reden, options).then((request) => request(this.axios, this.basePath));
    }
}



